import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { OfficeDb } from './db.js'
import type { Rejection } from './outbox.js'
import { backoffMs } from './outbox.js'
import { syncOnce, type SyncOutcome, type SyncTransport } from './sync.js'
import type { ClockState } from './clock.js'

/**
 * The sync loop (docs/02 §8).
 *
 * **This is deliberately not a 30-second timer.** docs/02 §8 is explicit:
 * "Do not poll on a 30-second timer — it is wasted bandwidth on the phones and
 * pointless load on the VPS." Every device already reads from its local mirror,
 * so the network is an update channel, never a read path, and there is nothing
 * on screen waiting for a poll to return.
 *
 * What actually wakes the loop:
 *
 *   - mount, once the db, transport and device id exist;
 *   - the `online` event — the single most useful trigger there is;
 *   - `visibilitychange` to visible, which is when a phone is being looked at;
 *   - an idle floor of 5–15 minutes as the backstop;
 *   - `syncNow()`, for a pull-to-refresh or a "sync now" button.
 *
 * Failures back off exponentially through `backoffMs`, which is the outbox's
 * policy and not a second one invented here.
 *
 * The status it returns is meant to be shown, not swallowed. A sync that has
 * been failing for an hour with six writes queued is something the three owners
 * should be able to see on their phones, and `lastError` and `rejections` are
 * what make that honest rather than a spinner that never resolves.
 */

export const MIN_IDLE_MS = 5 * 60_000
export const MAX_IDLE_MS = 15 * 60_000
export const DEFAULT_IDLE_MS = 10 * 60_000
/** Event triggers arriving inside this window of the last attempt are ignored. */
export const DEFAULT_MIN_TRIGGER_MS = 10_000

/**
 * Clamp the idle backstop into the 5–15 minute band from docs/02 §8. A caller
 * asking for 30 seconds is asking for the thing the document rules out, so the
 * floor is enforced here rather than trusted to every call site.
 */
export function idleFloorMs(requested?: number): number {
  if (requested === undefined || !Number.isFinite(requested)) return DEFAULT_IDLE_MS
  return Math.min(MAX_IDLE_MS, Math.max(MIN_IDLE_MS, requested))
}

/** Injectable so tests can drive the loop without a wall clock. */
export interface SyncScheduler {
  setTimeout(fn: () => void, ms: number): unknown
  clearTimeout(handle: unknown): void
}

const realScheduler: SyncScheduler = {
  setTimeout: (fn, ms) => globalThis.setTimeout(fn, ms),
  clearTimeout: h => { globalThis.clearTimeout(h as ReturnType<typeof globalThis.setTimeout>) },
}

export type SyncState = 'starting' | 'syncing' | 'idle' | 'error' | 'offline'

export type SyncTrigger = 'mount' | 'timer' | 'online' | 'visible' | 'manual'

export interface SyncStatus {
  state: SyncState
  /** What the platform says about connectivity. It lies sometimes; we still use it. */
  online: boolean
  /** Trusted-clock epoch of the last cycle that completed without error. */
  lastSyncedAt: number | null
  lastAttemptAt: number | null
  /** Writes still queued locally, refreshed after every cycle. */
  pendingCount: number
  /** Server refusals — terminal, never retried, and the user should see them. */
  rejections: Rejection[]
  lastError: string | null
  consecutiveFailures: number
  /** Device-clock instant the next automatic attempt is scheduled for. */
  nextAttemptAt: number | null
  lastTrigger: SyncTrigger | null
  syncNow: () => Promise<void>
  /** Re-read the local queue without touching the network. */
  refresh: () => Promise<void>
}

export interface UseSyncOptions {
  db: OfficeDb | null
  transport: SyncTransport | null
  deviceId: string | null
  /** Fed the server epoch after every cycle that reaches /time (docs/02 §4). */
  setClockState?: (s: ClockState) => void
  /** The DEVICE clock. Skew correction is exactly what this hook computes. */
  deviceNow?: () => number
  idleMs?: number
  minTriggerIntervalMs?: number
  enabled?: boolean
  scheduler?: SyncScheduler
  batchSize?: number
  maxPullRounds?: number
}

interface StatusFields {
  state: SyncState
  online: boolean
  lastSyncedAt: number | null
  lastAttemptAt: number | null
  pendingCount: number
  rejections: Rejection[]
  lastError: string | null
  consecutiveFailures: number
  nextAttemptAt: number | null
  lastTrigger: SyncTrigger | null
}

const INITIAL: StatusFields = {
  state: 'starting',
  online: true,
  lastSyncedAt: null,
  lastAttemptAt: null,
  pendingCount: 0,
  rejections: [],
  lastError: null,
  consecutiveFailures: 0,
  nextAttemptAt: null,
  lastTrigger: null,
}

const isOnline = (): boolean =>
  typeof navigator === 'undefined' ? true : navigator.onLine !== false

const message = (e: unknown): string => (e instanceof Error ? e.message : String(e))

export function useSync(opts: UseSyncOptions): SyncStatus {
  const { db, transport, deviceId } = opts
  const enabled = opts.enabled ?? true
  const idleMs = idleFloorMs(opts.idleMs)
  const minTriggerMs = opts.minTriggerIntervalMs ?? DEFAULT_MIN_TRIGGER_MS

  // Everything that must not restart the loop when it changes identity.
  const latest = useRef(opts)
  latest.current = opts

  const [status, setStatus] = useState<StatusFields>(() => ({ ...INITIAL, online: isOnline() }))

  const syncNowRef = useRef<() => Promise<void>>(async () => {})
  const refreshRef = useRef<() => Promise<void>>(async () => {})
  const syncNow = useCallback(() => syncNowRef.current(), [])
  const refresh = useCallback(() => refreshRef.current(), [])

  useEffect(() => {
    let cancelled = false
    let running = false
    let failures = 0
    let lastAttemptAt: number | null = null
    let timer: unknown = null

    // Read once per mount: a scheduler passed inline must not restart the loop.
    const sched = latest.current.scheduler ?? realScheduler
    const now = (): number => (latest.current.deviceNow ?? Date.now)()
    const patch = (p: Partial<StatusFields>): void => {
      if (!cancelled) setStatus(s => ({ ...s, ...p }))
    }

    const clear = (): void => {
      if (timer !== null) { sched.clearTimeout(timer); timer = null }
    }

    /** The local half of the status: queue depth and terminal rejections. */
    const readLocal = async (): Promise<void> => {
      if (!db) return
      try {
        const [pendingCount, rejections] = await Promise.all([
          db.outbox.count(),
          db.rejections.orderBy('at').reverse().limit(20).toArray(),
        ])
        patch({ pendingCount, rejections })
      } catch {
        // An unavailable IndexedDB must not take the app down; the board and the
        // phone both render from an empty context and say so.
      }
    }

    const schedule = (delay: number): void => {
      clear()
      if (cancelled || !enabled) return
      patch({ nextAttemptAt: now() + delay })
      timer = sched.setTimeout(() => { timer = null; void cycle('timer') }, delay)
    }

    const cycle = async (trigger: SyncTrigger): Promise<void> => {
      if (cancelled || !enabled) return
      if (!db || !transport || !deviceId) return
      if (running) return                       // one cycle at a time, ever

      if (!isOnline()) {
        // Nothing to gain from firing requests the platform already knows will
        // fail. The 'online' listener wakes us the instant that changes — but
        // navigator.onLine lies, and an 'online' event that never arrives would
        // otherwise strand the loop forever, so the idle backstop stays armed.
        // It costs one wake every 5–15 minutes and no network traffic at all.
        patch({ state: 'offline', online: false, lastTrigger: trigger })
        await readLocal()
        schedule(idleMs)
        return
      }
      // Rapid tab switching must not turn visibilitychange into a poll. Our own
      // timer is exempt — its delay IS the policy (backoff or the idle floor),
      // and a backoff shorter than this window would otherwise be swallowed and
      // take the whole loop with it. An explicit syncNow() always goes through.
      if (trigger !== 'manual' && trigger !== 'timer' && lastAttemptAt !== null) {
        const since = now() - lastAttemptAt
        if (since < minTriggerMs) {
          // Never drop the chain: a flap that lands inside the window must still
          // leave a retry behind it, or sync stops with nothing on screen saying so.
          if (timer === null) schedule(minTriggerMs - since)
          return
        }
      }

      running = true
      lastAttemptAt = now()
      clear()
      patch({ state: 'syncing', online: true, lastAttemptAt, lastTrigger: trigger })

      let outcome: SyncOutcome
      try {
        outcome = await syncOnce({
          db, transport, deviceId, deviceNow: now,
          ...(latest.current.batchSize !== undefined ? { batchSize: latest.current.batchSize } : {}),
          ...(latest.current.maxPullRounds !== undefined ? { maxPullRounds: latest.current.maxPullRounds } : {}),
        })
      } catch (e) {
        // syncOnce already swallows, but a loop that can throw is a loop that
        // can stop, and a stopped sync is invisible.
        outcome = { pushed: 0, rejected: 0, pulled: 0, rounds: 0, skipped: 0, skewMs: null, error: message(e) }
      } finally {
        running = false
      }

      // The server epoch is authoritative over the device's own (docs/02 §4).
      // Worth taking even from a cycle that later failed: /time answered, so the
      // correction is real, and clock trust is shown on the board.
      if (outcome.skewMs !== null && !cancelled) {
        const skewMs = outcome.skewMs
        latest.current.setClockState?.({ skewMs, lastSyncedAt: now() + skewMs })
      }

      await readLocal()
      if (cancelled) return

      if (outcome.error !== null) {
        failures += 1
        patch({ state: 'error', lastError: outcome.error, consecutiveFailures: failures })
        // The outbox owns backoff. First retry is quick because most failures
        // are a blip; it climbs to a one-minute ceiling from there.
        schedule(backoffMs(failures - 1))
      } else {
        failures = 0
        patch({
          state: 'idle',
          lastError: null,
          consecutiveFailures: 0,
          lastSyncedAt: now() + (outcome.skewMs ?? 0),
        })
        schedule(idleMs)
      }
    }

    syncNowRef.current = () => cycle('manual')
    refreshRef.current = readLocal

    const onOnline = (): void => { patch({ online: true }); void cycle('online') }
    const onOffline = (): void => {
      // Drop the pending retry — there is nothing to retry against — but fall
      // back to the idle backstop rather than to nothing. If the 'online' event
      // is missed (it is, on a WebView that suspends), that timer is the only
      // thing that will ever notice the network came back.
      patch({ online: false, state: 'offline' })
      schedule(idleMs)
    }
    const onVisibility = (): void => {
      if (typeof document !== 'undefined' && document.visibilityState === 'visible') void cycle('visible')
    }

    if (typeof window !== 'undefined') {
      window.addEventListener('online', onOnline)
      window.addEventListener('offline', onOffline)
    }
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', onVisibility)
    }

    void readLocal()
    void cycle('mount')

    return () => {
      cancelled = true
      clear()
      if (typeof window !== 'undefined') {
        window.removeEventListener('online', onOnline)
        window.removeEventListener('offline', onOffline)
      }
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', onVisibility)
      }
    }
  }, [db, transport, deviceId, enabled, idleMs, minTriggerMs])

  return useMemo<SyncStatus>(() => ({ ...status, syncNow, refresh }), [status, syncNow, refresh])
}
